<!--
  Created 01.01.2024
  Originated from Tailwind UI component: Forms, ComboBoxes, WithSecondaryText
  https://tailwindui.com/components/application-ui/forms/comboboxes#component-0834ed9ce704a78cc0045ef92f4af723
-->
<template>
  <Combobox
    v-model="selectedItems"
    :class="classes"
    as="div"
    :multiple="hasMultiple"
    :nullable="isNullable">
    <ComboboxLabel
      v-if="label"
      class="block text-sm font-medium leading-6 text-gray-900">
      {{ label }}
    </ComboboxLabel>

    <!-- If multiple, we display chosen values as removable boxes above input -->
    <div
      v-if="hasMultiple && showListAboveInput"
      class="w-full">
      <div
        v-if="selectedItems && selectedItems.length > 0"
        class="mt-2 space-x-2">
        <span
          v-for="(item, index) in selectedItems"
          :key="'selectedItems-' + item.id"
          class="inline-flex items-center rounded-full bg-indigo-100 py-1 pl-2 pr-0.5 text-xs font-medium text-indigo-700">
          <slot
            name="label"
            :item="item">
            {{ item.name }}
          </slot>
          <button
            v-if="!item.readOnly"
            type="button"
            class="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full text-indigo-400 hover:bg-indigo-200
              hover:text-indigo-500 focus:bg-indigo-500 focus:text-white focus:outline-none"
            @click="deselectItem(item, index)">
            <span class="sr-only">Remove {{ item.name }} from selection</span>
            <svg
              class="size-2"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 8 8">
              <path
                stroke-linecap="round"
                stroke-width="1.5"
                d="M1 1l6 6m0-6L1 7" />
            </svg>
          </button>
        </span>
      </div>
    </div>

    <div class="relative mt-2">
      <ComboboxButton class="group w-full">
        <ComboboxInput
          class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset
            ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600
            disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 sm:text-sm sm:leading-6"
          autocomplete="off"
          :placeholder="placeholder"
          :display-value="hasMultiple ? undefined : (item) => item?.primaryText"
          @change="query = $event.target.value" />
        <div class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none group-disabled:cursor-not-allowed">
          <ChevronUpDownIcon
            class="size-5 text-gray-600 group-disabled:text-gray-300"
            aria-hidden="true" />
        </div>
      </ComboboxButton>

      <ComboboxOptions
        v-if="filteredItems.length > 0"
        class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg
          ring-1 ring-black/5 focus:outline-none sm:text-sm">
        <ComboboxOption
          v-for="item in filteredItems"
          :key="'filteredItems-' + item.id"
          v-slot="{ active, selected }"
          :disabled="item.readOnly"
          :value="item"
          as="template">
          <li
            :class="[
              'relative cursor-default select-none py-2 pl-3 pr-9',
              active ? 'bg-indigo-600 text-white' : 'text-gray-900',
              item.readOnly ? 'cursor-not-allowed bg-amber-100 text-amber-700' : '']">
            <div class="flex">
              <span :class="['truncate', selected && 'font-semibold']">
                {{ item.primaryText }}
              </span>
              <span
                :class="[
                  'ml-2 truncate text-gray-500',
                  active ? (item.readOnly ? 'text-gray-500' : 'text-indigo-200') : 'text-gray-500']">
                {{ item.secondaryText }}
              </span>
            </div>

            <span
              v-if="selected"
              :class="[
                'absolute inset-y-0 right-0 flex items-center pr-4',
                active ? 'text-white' : 'text-indigo-600',
                item.readOnly ? 'text-amber-700' : ''
              ]">
              <CheckIcon
                class="size-5"
                aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>

<script setup>
import {
  computed, ref,
} from 'vue';
import {
  CheckIcon, ChevronUpDownIcon,
} from '@heroicons/vue/24/solid';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const props = defineProps({
  modelValue: {
    type: Array,
    required: false,
    default: () => [],
  },
  classes: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Search for or select an item',
  },
  hasMultiple: {
    type: Boolean,
    required: false,
    default: false,
  },
  showListAboveInput: {
    type: Boolean,
    required: false,
    default: true,
  },
  isNullable: {
    type: Boolean,
    required: false,
    default: true,
  },
  items: {
    type: Array,
    required: true,
    default: () => [
      {
        id: 1,
        primaryText: 'Drammen',
        secondaryText: 'Norway',
        selected: false,
        readOnly: false,
      },
      {
        id: 2,
        primaryText: 'Barcelona',
        secondaryText: 'Spain',
        selected: true,
        readOnly: true,
      },
    ],
  },
});

const emit = defineEmits(['update:modelValue']);
const query = ref('');

const items = computed(() => props.items);

/**
 * If multiple, we display chosen values as removable boxes above input and synchronise selection with combobox options
 */
const selectedItems = computed({
  get() {
    // If not multiple, we display the selected value as a single box
    if (!props.hasMultiple) {
      return props.items.filter((item) => item.id === props.modelValue.id);
    }

    // If modelValue is empty, filter defaults and update parent
    if (!props.modelValue || props.modelValue.length === 0) {
      const defaults = props.items.filter((item) => item.selected);
      if (defaults.length > 0) {
        emit('update:modelValue', defaults);

        return defaults;
      }

      return [];
    }

    // If there is already a set modelValue, filter and sort based on it.
    const selected = props.items.filter((item) => props.modelValue.some((modelValueItem) => modelValueItem.id === item.id));

    return selected.sort(
      (a, b) => props.modelValue.findIndex((modelValueItem) => modelValueItem.id === a.id)
        - props.modelValue.findIndex((modelValueItem) => modelValueItem.id === b.id),
    );
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

/**
 * Search functionality for the combobox
 */
const filteredItems = computed(() => {
  const queryLower = query.value.toLowerCase();

  return (query.value === ''
    ? items.value
    : items.value.filter((item) => item.primaryText.toLowerCase().startsWith(queryLower)
        || item.secondaryText.toLowerCase().startsWith(queryLower))
  );
});

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 *  Deselect an item from the list of selected items
 * @param {object} item - the item to deselect
 * @param {number} index - the index of the item to deselect
 */
function deselectItem(item, index) {
  const newArray = [...selectedItems.value];
  newArray.splice(index, 1);
  selectedItems.value = newArray;
}

/**
 * Give parent access to the deselectItem function
 */
defineExpose({
  deselectItem,
});

</script>
